import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  Printer,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OfflineBanner } from '@/components/pos/OfflineBanner';
import { login } from '@/api/auth.api';
import { getUserDevice } from '@/api/device.api';
import { useAuthStore } from '@/stores/auth.store';
import { useDeviceStore } from '@/stores/device.store';
import { env } from '@/lib/env';
import { canOperatePos } from '@/lib/pos-roles';
import type { ApiError } from '@/types/api';

const schema = z.object({
  email: z.string().trim().min(1, 'Email wajib diisi').email('Format email tidak valid'),
  password: z.string().min(1, 'Kata sandi wajib diisi'),
});
type FormValues = z.infer<typeof schema>;



/** Crisp white render of the high-res primary logo (the dedicated white asset is
 * low-resolution and looks soft on the brand panel). */
const WHITE_LOGO_FILTER = 'brightness(0) invert(1)';

/** Subtle racing checkerboard — a nod to the Autopia heritage flag mark. */
const CHECKER_PATTERN: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg, rgba(255,215,0,0.05) 25%, transparent 25% 75%, rgba(255,215,0,0.05) 75%), linear-gradient(45deg, rgba(255,215,0,0.05) 25%, transparent 25% 75%, rgba(255,215,0,0.05) 75%)',
  backgroundSize: '26px 26px',
  backgroundPosition: '0 0, 13px 13px',
};

const HIGHLIGHTS = [
  { icon: Zap, label: 'Transaksi kilat di jam sibuk' },
  { icon: Printer, label: 'Cetak tiket & struk instan' },
  { icon: ShieldCheck, label: 'Sesi aman, terikat perangkat' },
];

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const hydrateDevice = useDeviceStore((s) => s.hydrateFromBinding);
  const [serverError, setServerError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema), mode: 'onSubmit' });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      const res = await login(values.email, values.password);
      if (!canOperatePos(res.user.role)) {
        setServerError('Akun ini tidak memiliki akses kasir ticketbox.');
        return;
      }
      if (res.user.status !== 'ACTIVE') {
        setServerError('Akun tidak aktif. Hubungi administrator.');
        return;
      }
      const sessionId =
        typeof res.session === 'string' ? res.session : (res.session?.sessionId ?? res.session?.id ?? null);
      setSession({ tokens: res.tokens, user: res.user, sessionId });

      // Probe the device binding to decide the landing route.
      let bound = false;
      try {
        const binding = await getUserDevice();
        hydrateDevice(binding);
        bound = Boolean(binding?.ticketPrinterId);
      } catch {
        /* binding probe failures land us on /setup */
      }

      const from = (location.state as { from?: string } | null)?.from;
      navigate(bound ? (from && from !== '/login' ? from : '/') : '/setup', { replace: true });
    } catch (err) {
      const apiErr = err as ApiError;
      setServerError(
        apiErr?.isNetwork
          ? 'Tidak dapat terhubung ke server.'
          : (apiErr?.message ?? 'Email atau kata sandi salah.'),
      );
    }
  });

  return (
    <div className="grid h-screen w-screen grid-cols-1 overflow-hidden lg:grid-cols-5">
      {/* ─── Brand panel ─── */}
      <aside className="rail-surface relative hidden flex-col justify-between overflow-hidden p-12 text-cream-50 lg:col-span-2 lg:flex">
        {/* Layered atmosphere */}
        <div className="pointer-events-none absolute inset-0" style={CHECKER_PATTERN} />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(120% 80% at 15% 0%, rgba(255,215,0,0.18) 0, transparent 50%), radial-gradient(90% 70% at 100% 100%, rgba(192,57,43,0.45) 0, transparent 55%)',
          }}
        />
        {/* Ticket-stub perforation along the seam */}
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-px"
          style={{
            backgroundImage:
              'radial-gradient(circle, rgba(255,251,243,0.35) 1.5px, transparent 1.6px)',
            backgroundSize: '2px 16px',
            backgroundRepeat: 'repeat-y',
          }}
        />

        <div className="relative animate-reveal-up">
          <img
            src="/logo-autopia-primary.png"
            alt={env.BRAND_NAME}
            className="h-12 w-auto"
            style={{ filter: WHITE_LOGO_FILTER }}
          />
        </div>

        <div className="relative space-y-8">
          <div className="space-y-4 animate-reveal-up" style={{ animationDelay: '80ms' }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-gold-500/30 bg-gold-500/10 px-3 py-1 font-brand text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-gold-200">
              <span className="h-1.5 w-1.5 rounded-full bg-gold-500" />
              Point of Sale · Ticketbox
            </span>
            <h1 className="font-display text-5xl font-extrabold leading-[1.05] tracking-tight">
              Loket Tiket
              <br />
              <span className="text-gold-500">{env.BRAND_NAME}</span>
            </h1>
            <p className="max-w-sm font-sans text-base leading-relaxed text-cream-100/75">
              Sistem kasir loket — cepat, presisi, dan siap melayani antrean panjang tanpa
              kehilangan satu pun penjualan.
            </p>
          </div>

          <ul
            className="space-y-3 animate-reveal-up"
            style={{ animationDelay: '160ms' }}
          >
            {HIGHLIGHTS.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-3 text-sm text-cream-100/85">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5 ring-1 ring-gold-500/25">
                  <Icon className="h-4 w-4 text-gold-500" />
                </span>
                {label}
              </li>
            ))}
          </ul>
        </div>

        <p
          className="relative font-sans text-xs text-cream-100/45 animate-reveal-up"
          style={{ animationDelay: '240ms' }}
        >
          © {new Date().getFullYear()} {env.BRAND_NAME} Museum &amp; Parks · Point of Sale
          Ticketbox
        </p>
      </aside>

      {/* ─── Login column ─── */}
      <div className="flex min-h-0 flex-col lg:col-span-3">
        <OfflineBanner />
        <div className="flex flex-1 items-center justify-center overflow-y-auto px-6 py-10">
          <div className="w-full max-w-sm animate-reveal-up">
            <div className="mb-10 flex justify-center lg:hidden">
              <img src="/logo-autopia-primary.png" alt={env.BRAND_NAME} className="h-11 w-auto" />
            </div>

            <div className="space-y-1.5">
              <h2 className="font-display text-3xl font-bold tracking-tight text-ink-900">
                Masuk Kasir
              </h2>
              <p className="text-sm text-muted-foreground">
                Gunakan akun kasir ticketbox Anda untuk membuka loket.
              </p>
            </div>

            <form onSubmit={onSubmit} className="mt-9 space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <div className="group relative">
                  <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-400 transition-colors group-focus-within:text-primary" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    autoFocus
                    placeholder="kasir@autopia.id"
                    className="h-14 pl-11"
                    {...register('email')}
                  />
                </div>
                {errors.email && (
                  <p className="text-xs font-medium text-destructive">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password">Kata sandi</Label>
                <div className="group relative">
                  <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-400 transition-colors group-focus-within:text-primary" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="h-14 pl-11 pr-12"
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
                    aria-pressed={showPassword}
                    className="absolute right-1.5 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-ink-400 transition-colors hover:bg-secondary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs font-medium text-destructive">{errors.password.message}</p>
                )}
              </div>

              {serverError && (
                <div className="flex items-start gap-2 rounded-lg border-2 border-destructive/25 bg-destructive/5 px-4 py-3 text-sm font-medium text-destructive">
                  {serverError}
                </div>
              )}

              <Button
                type="submit"
                size="lg"
                className="group h-14 w-full text-lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    Masuk
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </Button>
            </form>

            <p className="mt-8 text-center text-xs text-ink-400">
              Kesulitan masuk? Hubungi administrator loket Anda.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
